/* AI upscale disabled at launch (Direktør 2026-10-05) */
const LTD_AI_UPSCALE_ENABLED = true; /* KUN temakopi 207715008844 (AI-opskalering fase 1). Live-temaet: false. */
/* Lavet til dig – "Print dit eget billede" (print mode in sections/ltd-product.liquid).
 * Variant choice per option, DPI check of the chosen file and required checkboxes.
 * Vanilla JS, no dependencies. Texts come from locales via [data-print-strings]. */
(() => {
  'use strict';

  const TIER_GOOD = 150;
  const TIER_OK = 100;
  const TIER_LOW = 72;
  const MIN_PIXELS = 1000000;
  const HEADER_BYTES = 4 * 1024 * 1024;
  const DECODE_MAX_BYTES = 80 * 1024 * 1024;

  const fill = (text, values) =>
    String(text || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

  const readJson = (element) => {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      return null;
    }
  };

  /** Parses "A4 (21 × 29,7 cm)" or "60 × 90 cm" into centimetres. */
  const parseSize = (label) => {
    const match = String(label || '').match(/(\d+(?:[.,]\d+)?)\s*[×x]\s*(\d+(?:[.,]\d+)?)\s*cm/i);
    if (!match) return null;
    const a = parseFloat(match[1].replace(',', '.'));
    const b = parseFloat(match[2].replace(',', '.'));
    return a > 0 && b > 0 ? { a, b } : null;
  };

  /** DPI when the image fills the print (the weakest axis decides); rotation is allowed. */
  const dpiFor = (pixels, size) => {
    const a = size.a / 2.54;
    const b = size.b / 2.54;
    const upright = Math.min(pixels.width / a, pixels.height / b);
    const rotated = Math.min(pixels.width / b, pixels.height / a);
    return Math.floor(Math.max(upright, rotated));
  };

  const sniffType = (bytes, file) => {
    if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'jpeg';
    if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'png';
    if (bytes.length >= 4 && ((bytes[0] === 0x49 && bytes[1] === 0x49 && bytes[2] === 0x2a) || (bytes[0] === 0x4d && bytes[1] === 0x4d && bytes[3] === 0x2a))) return 'tiff';
    if (bytes.length >= 4 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) return 'pdf';
    const type = (file.type || '').toLowerCase();
    if (type === 'image/jpeg') return 'jpeg';
    if (type === 'image/png') return 'png';
    return 'other';
  };

  const jpegSize = (view) => {
    let offset = 2;
    while (offset + 9 < view.byteLength) {
      if (view.getUint8(offset) !== 0xff) {
        offset += 1;
        continue;
      }
      const marker = view.getUint8(offset + 1);
      if (marker === 0xff) {
        offset += 1;
        continue;
      }
      if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
        offset += 2;
        continue;
      }
      const isFrame = marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
      if (isFrame) {
        const height = view.getUint16(offset + 5);
        const width = view.getUint16(offset + 7);
        return width > 0 && height > 0 ? { width, height } : null;
      }
      offset += 2 + view.getUint16(offset + 2);
    }
    return null;
  };

  const pngSize = (view) => {
    if (view.byteLength < 24) return null;
    const width = view.getUint32(16);
    const height = view.getUint32(20);
    return width > 0 && height > 0 ? { width, height } : null;
  };

  const decodeSize = async (file) => {
    if (file.size > DECODE_MAX_BYTES) return null;
    if ('createImageBitmap' in window) {
      try {
        const bitmap = await createImageBitmap(file);
        const result = { width: bitmap.width, height: bitmap.height };
        if (bitmap.close) bitmap.close();
        return result;
      } catch (error) {
        /* fall back to <img> */
      }
    }
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image.naturalWidth && image.naturalHeight ? { width: image.naturalWidth, height: image.naturalHeight } : null);
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      image.src = url;
    });
  };

  /** Blob.arrayBuffer with a FileReader fallback for older browsers. */
  const readBuffer = (blob) => {
    if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(blob);
    });
  };

  /** Returns { kind: 'image', width, height } or { kind: 'manual' }. */
  const inspectFile = async (file) => {
    const head = await readBuffer(file.slice(0, Math.min(file.size, HEADER_BYTES)));
    const bytes = new Uint8Array(head);
    const type = sniffType(bytes, file);
    if (type !== 'jpeg' && type !== 'png') return { kind: 'manual' };
    const view = new DataView(head);
    const size = (type === 'jpeg' ? jpegSize(view) : pngSize(view)) || (await decodeSize(file));
    return size ? { kind: 'image', width: size.width, height: size.height } : { kind: 'manual' };
  };

  const setup = (checks) => {
    if (checks.dataset.printReady === 'true') return;
    const form = document.getElementById(checks.dataset.formId);
    if (!form) return;
    checks.dataset.printReady = 'true';

    const section = form.closest('[data-personalizer]') || form;
    const fields = form.querySelector('[data-print-fields]');
    const strings = readJson(checks.querySelector('[data-print-strings]')) || {};
    const data = (fields && readJson(fields.querySelector('[data-print-variants]'))) || { options: [], variants: [] };
    const variantSelect = form.querySelector('[data-print-variant-select]');
    const optionSelects = [...form.querySelectorAll('[data-print-option]')];
    const status = checks.querySelector('[data-print-status]');
    const statusText = checks.querySelector('[data-print-status-text]');
    const statusExtra = checks.querySelector('[data-print-status-extra]');
    const acceptWrap = checks.querySelector('[data-print-accept-wrap]');
    const acceptLow = checks.querySelector('[data-print-accept-low]');
    const upscaleWrap = checks.querySelector('[data-print-upscale-wrap]');
    const upscale = checks.querySelector('[data-print-upscale]');
    const rights = checks.querySelector('[data-print-rights]');
    const errorBox = checks.querySelector('[data-print-error]');
    const props = {
      dpi: checks.querySelector('[data-print-prop="dpi"]'),
      pixels: checks.querySelector('[data-print-prop="pixels"]'),
      quality: checks.querySelector('[data-print-prop="quality"]'),
    };
    const submitButtons = () => [...form.querySelectorAll('button[type="submit"], input[type="submit"]')];

    const sizeIndex = Math.max(0, data.options.findIndex((name) => /størrelse|size/i.test(name)));
    const mediumIndex = data.options.length > 1 ? (sizeIndex === 0 ? 1 : 0) : -1;

    let file = null;
    let pixels = null;
    let kind = 'none';
    let tier = 'waiting';
    let token = 0;

    const currentVariant = () => {
      const id = variantSelect ? Number(variantSelect.value) : NaN;
      return data.variants.find((variant) => variant.id === id) || null;
    };

    const findVariant = (values) =>
      data.variants.find((variant) => variant.options.every((value, index) => value === values[index])) || null;

    /* ---------- variant choice: one select per option ---------- */
    const syncOptionStates = () => {
      if (mediumIndex < 0) return;
      const sizeSelect = optionSelects[sizeIndex];
      const mediumSelect = optionSelects[mediumIndex];
      if (!sizeSelect || !mediumSelect) return;
      for (const option of mediumSelect.options) {
        const values = [];
        values[sizeIndex] = sizeSelect.value;
        values[mediumIndex] = option.value;
        const match = findVariant(values);
        const possible = Boolean(match && match.available);
        option.disabled = !possible;
        const base = option.dataset.label || option.textContent;
        option.dataset.label = base;
        option.textContent = possible ? base : `${base} – ${strings.unavailable || ''}`.trim();
      }
    };

    const applyOptions = (changedIndex) => {
      if (!variantSelect || !optionSelects.length) return;
      const values = optionSelects.map((select) => select.value);
      let match = findVariant(values);
      if (!match || !match.available) {
        /* keep the option the customer just changed and pick the first possible partner */
        match =
          data.variants.find((variant) => variant.available && variant.options[changedIndex] === values[changedIndex]) ||
          data.variants.find((variant) => variant.available) ||
          null;
        if (match) optionSelects.forEach((select, index) => (select.value = match.options[index]));
      }
      if (match) variantSelect.value = String(match.id);
      syncOptionStates();
      evaluate();
    };

    if (variantSelect && optionSelects.length && data.variants.length) {
      const start = currentVariant();
      if (start) optionSelects.forEach((select, index) => (select.value = start.options[index]));
      form.querySelectorAll('[data-print-option-wrap]').forEach((wrap) => (wrap.hidden = false));
      const fallback = form.querySelector('[data-print-fallback]');
      if (fallback) fallback.hidden = true;
      optionSelects.forEach((select, index) => select.addEventListener('change', () => applyOptions(index)));
      syncOptionStates();
    }
    if (variantSelect) variantSelect.addEventListener('change', () => evaluate());

    /* ---------- quality status ---------- */
    const setProp = (input, value) => {
      if (!input) return;
      input.value = value || '';
      input.disabled = !value;
    };

    const setAcceptLow = (needed) => {
      if (!acceptWrap || !acceptLow) return;
      acceptWrap.hidden = !needed;
      acceptLow.disabled = !needed;
      acceptLow.required = needed;
      if (!needed) acceptLow.checked = false;
    };

    /* Free AI upscaling with proof: only a line item property, never a product or a price. */
    const setUpscale = (offered) => {
      if (!upscaleWrap || !upscale) return;
      const allowUpscale = false && offered; /* fase 1: det gamle flueben er erstattet af uploadwidgettens «Forbedr automatisk» */
      upscaleWrap.hidden = !allowUpscale;
      upscale.disabled = !allowUpscale;
      if (!allowUpscale) upscale.checked = false;
    };

    const setBlocked = (blocked) => {
      for (const button of submitButtons()) {
        if (blocked) {
          button.disabled = true;
          button.dataset.printBlocked = 'true';
        } else if (button.dataset.printBlocked === 'true') {
          button.disabled = false;
          delete button.dataset.printBlocked;
        }
      }
    };

    const contactLink = () => {
      const link = document.createElement('a');
      link.className = 'ltd-text-link';
      link.href = checks.dataset.contactUrl || '/';
      link.textContent = `${strings.contact || ''} →`;
      return link;
    };

    const show = (nextTier, text, extra, withContact) => {
      tier = nextTier;
      status.dataset.tier = nextTier;
      statusText.textContent = text;
      statusExtra.replaceChildren();
      for (const line of [].concat(extra || [])) {
        if (line) statusExtra.append(document.createTextNode(`${line} `));
      }
      if (withContact) statusExtra.append(contactLink());
      statusExtra.hidden = !statusExtra.childNodes.length;
    };

    const suggestion = (variant) => {
      if (!pixels || !variant) return null;
      let best = null;
      for (const candidate of data.variants) {
        if (!candidate.available) continue;
        if (mediumIndex >= 0 && candidate.options[mediumIndex] !== variant.options[mediumIndex]) continue;
        const size = parseSize(candidate.options[sizeIndex]);
        if (!size) continue;
        const dpi = dpiFor(pixels, size);
        if (dpi < TIER_OK) continue;
        if (!best || size.a * size.b > best.area) best = { area: size.a * size.b, label: candidate.options[sizeIndex], dpi };
      }
      return best;
    };

    function evaluate() {
      if (errorBox) errorBox.hidden = true;
      const variant = currentVariant();
      const sizeLabel = variant ? variant.options[sizeIndex] : '';
      const size = parseSize(sizeLabel);

      if (kind === 'none') {
        show('waiting', strings.waiting || '');
        ['dpi', 'pixels', 'quality'].forEach((key) => setProp(props[key], ''));
        setAcceptLow(false);
        setUpscale(false);
        setBlocked(false);
        return;
      }
      if (kind === 'reading') {
        show('waiting', strings.reading || '');
        return;
      }
      if (kind === 'manual' || !size || !pixels) {
        show('manual', strings.manual || '');
        setProp(props.dpi, '');
        setProp(props.pixels, pixels ? `${pixels.width} × ${pixels.height}` : '');
        setProp(props.quality, strings.propManual || '');
        setAcceptLow(false);
        setUpscale(false);
        setBlocked(false);
        return;
      }

      const dpi = dpiFor(pixels, size);
      const px = `${pixels.width} × ${pixels.height}`;
      const values = { dpi, size: sizeLabel, px };
      setProp(props.dpi, String(dpi));
      setProp(props.pixels, px);

      if (pixels.width * pixels.height < MIN_PIXELS || dpi < TIER_LOW) {
        const tiny = pixels.width * pixels.height < MIN_PIXELS;
        const tip = tiny ? null : suggestion(variant);
        show(
          'bad',
          fill(tiny ? strings.tiny : strings.bad, values),
          [tip ? fill(strings.suggest, { size: tip.label, dpi: tip.dpi }) : '', strings.upscaleBad || ''],
          true
        );
        setProp(props.quality, '');
        setAcceptLow(false);
        setUpscale(false);
        setBlocked(true);
        return;
      }
      setBlocked(false);
      if (dpi >= TIER_GOOD) {
        show('good', fill(strings.good, values));
        setProp(props.quality, fill(strings.propGood, values));
        setAcceptLow(false);
        setUpscale(false);
      } else if (dpi >= TIER_OK) {
        show('ok', fill(strings.ok, values));
        setProp(props.quality, fill(strings.propOk, values));
        setAcceptLow(false);
        setUpscale(false);
      } else {
        const tip = suggestion(variant);
        show('low', fill(strings.low, values), tip ? fill(strings.suggest, { size: tip.label, dpi: tip.dpi }) : '');
        setProp(props.quality, fill(strings.propLow, values));
        setAcceptLow(true);
        setUpscale(true);
      }
    }

    /* ---------- file detection (own field, upload app fields, drag and drop) ---------- */
    const handleFile = async (chosen) => {
      const current = ++token;
      file = chosen || null;
      pixels = null;
      if (!file) {
        kind = 'none';
        evaluate();
        return;
      }
      kind = 'reading';
      evaluate();
      let result = { kind: 'manual' };
      try {
        result = await inspectFile(file);
      } catch (error) {
        result = { kind: 'manual' };
      }
      if (current !== token) return;
      kind = result.kind;
      pixels = result.kind === 'image' ? { width: result.width, height: result.height } : null;
      evaluate();
    };

    const belongsHere = (input) => input.form === form || form.contains(input) || section.contains(input);

    document.addEventListener(
      'change',
      (event) => {
        const path = event.composedPath ? event.composedPath() : [event.target];
        const input = path.find((node) => node instanceof HTMLInputElement && node.type === 'file');
        if (!input || !belongsHere(input)) return;
        handleFile(input.files && input.files.length ? input.files[input.files.length - 1] : null);
      },
      true
    );
    section.addEventListener(
      'drop',
      (event) => {
        const files = event.dataTransfer && event.dataTransfer.files;
        if (files && files.length) handleFile(files[files.length - 1]);
      },
      true
    );

    /* ---------- submit guard ---------- */
    const fail = (message, focusTarget) => {
      if (errorBox) {
        errorBox.textContent = message;
        errorBox.hidden = false;
      }
      if (focusTarget) focusTarget.focus();
    };

    form.addEventListener(
      'submit',
      (event) => {
        let message = '';
        let target = null;
        if (tier === 'bad') {
          message = strings.badError || strings.bad || '';
          target = status;
        } else if (tier === 'low' && acceptLow && !acceptLow.checked) {
          message = strings.lowError || '';
          target = acceptLow;
        } else if (rights && !rights.checked) {
          message = strings.rightsError || '';
          target = rights;
        }
        if (!message) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        fail(message, target);
      },
      true
    );

    if (rights) rights.addEventListener('change', () => errorBox && (errorBox.hidden = true));
    if (acceptLow) acceptLow.addEventListener('change', () => errorBox && (errorBox.hidden = true));

    /* Product image thumbnails (print mode): ltd-store.js swaps the src; keep alt text and the
       current thumbnail in sync so screen readers get the right description. */
    section.querySelectorAll('[data-gallery-alt]').forEach((button) => {
      button.addEventListener('click', () => {
        const image = section.querySelector('.ltd-product-photo img');
        if (image) image.alt = button.dataset.galleryAlt || '';
        section.querySelectorAll('[data-gallery-alt]').forEach((other) => other.removeAttribute('aria-current'));
        button.setAttribute('aria-current', 'true');
      });
    });

    /* Upload-Lift exposes its field on window "upload:ready" (event.detail.updateConfig). */
    window.addEventListener('upload:ready', (event) => {
      checks.dataset.uploadLift = event && event.detail ? 'ready' : 'unknown';
    });

    /* AI-opskalering (fase 1): kundens valg i uploadwidgetten (forbedret/original) styrer DPI-trinnet.
       Rød original kan bruges, hvis den forbedrede fil når gul eller grøn. */
    if (LTD_AI_UPSCALE_ENABLED) {
      form.addEventListener('lvd:choice', (event) => {
        const d = event.detail || {};
        if (!d.width || !d.height) return;
        token++;
        kind = 'image';
        pixels = { width: d.width, height: d.height };
        evaluate();
        if (d.choice === 'enhanced') setProp(props.quality, `${(props.quality && props.quality.value) || ''} · AI-forbedret`.replace(/^ · /, ''));
        if (d.choice === 'auto') setProp(props.quality, `${(props.quality && props.quality.value) || ''} · AI-forbedres før print`.replace(/^ · /, ''));
      });
    }

    /* A file may already be selected (back navigation / restored form state). */
    const existing = [...form.querySelectorAll('input[type="file"]')].find((input) => input.files && input.files.length);
    if (existing) handleFile(existing.files[existing.files.length - 1]);
    else evaluate();
  };

  const init = () => document.querySelectorAll('[data-print-checks]').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
